/**
 * The picture dialog's decisions, kept out of `PictureDialog.tsx` (learner
 * picture PRD §3.2, §7.2).
 */
import { clampCrop, minScale, type Crop, type PictureView } from '../profile/picture.ts';
import type { Register } from '../profile/register.ts';

export type PictureTab = 'presets' | 'photo';

/** PRD §3.2: cartoons first for a child, without hiding the upload. */
export function openingTab(register: Register, current: PictureView['kind']): PictureTab {
  return register === 'adult' && current === 'photo' ? 'photo' : 'presets';
}

/** The radio grid's roving focus; `null` for a key it does not handle. */
export function gridMove(key: string, index: number, count: number, columns: number): number | null {
  switch (key) {
    case 'ArrowRight':
      return (index + 1) % count;
    case 'ArrowLeft':
      return (index - 1 + count) % count;
    case 'ArrowDown':
      return (index + columns) % count;
    case 'ArrowUp':
      return (index - columns + count) % count;
    case 'Home':
      return 0;
    case 'End':
      return count - 1;
    default:
      return null;
  }
}

/** Past four times "just covering", a 512 px crop of most phone photos is upscaled mush. */
const MAX_ZOOM = 4;

export function zoomRange(w: number, h: number): { min: number; max: number } {
  const min = minScale(w, h);
  return { min, max: min * MAX_ZOOM };
}

/**
 * The slider's spoken value. Its raw value is the source-to-output scale,
 * which means nothing to a listener; 100 % is the photo just covering the
 * square, the slider's own floor.
 */
export function zoomPercent(scale: number, w: number, h: number): number {
  return Math.round((scale / minScale(w, h)) * 100);
}

export function initialCrop(w: number, h: number): Crop {
  return clampCrop({ cx: w / 2, cy: h / 2, scale: minScale(w, h) }, w, h);
}

/**
 * `dx`/`dy` are CSS px on a crop view `viewPx` wide; the image follows the
 * pointer, so the centre moves the other way.
 */
export function dragCrop(crop: Crop, dx: number, dy: number, viewPx: number, w: number, h: number, out = 512): Crop {
  const perCss = out / viewPx / crop.scale;
  return clampCrop({ ...crop, cx: crop.cx - dx * perCss, cy: crop.cy - dy * perCss }, w, h, out);
}

/** One keyboard nudge of the crop, in CSS px. */
const NUDGE = 8;

/**
 * An arrow key as a drag of `NUDGE` CSS px the arrow's way, so the photo moves
 * the way the arrow points, as it does under a finger (PRD §3.2); `null` for a
 * key the crop does not handle.
 */
export function nudgeFor(key: string): [dx: number, dy: number] | null {
  switch (key) {
    case 'ArrowLeft':
      return [-NUDGE, 0];
    case 'ArrowRight':
      return [NUDGE, 0];
    case 'ArrowUp':
      return [0, -NUDGE];
    case 'ArrowDown':
      return [0, NUDGE];
    default:
      return null;
  }
}

export function zoomTo(crop: Crop, scale: number, w: number, h: number): Crop {
  const { min, max } = zoomRange(w, h);
  return clampCrop({ ...crop, scale: Math.min(Math.max(scale, min), max) }, w, h);
}

/** Exponential, so each notch is the same proportion at any zoom. */
export function wheelScale(scale: number, deltaY: number): number {
  return scale * Math.exp(-deltaY * 0.002);
}
